import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { authHeader, getSession } from "../../auth";

/**
 * TeamCheckPage — page PUBLIQUE de validation des inscriptions par les
 * chefs d'équipe (lien /team-check/<course>?t=<jeton>, diffusé par
 * WhatsApp). Ajouté le 01.10.2026, backend : teamCheckRouter.js.
 *
 * L'entraîneur choisit son groupe (association ou inscription selon le
 * réglage de la course), retire ou ajoute des athlètes (listes FIS /
 * Swiss-Ski), puis confirme : le groupe est verrouillé. Un chronométreur
 * connecté peut le réouvrir depuis cette même page.
 * Pensée pour le téléphone (une colonne, gros boutons).
 */

// Page publique en ANGLAIS (entraîneurs de toutes nations) — 07.10.2026.
const GROUP_LABEL = { association: "association", inscription: "entry group", club: "club", nation: "nation", category: "category" };

export default function TeamCheckPage() {
    const { eventKey } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("t") || "";
    const group = searchParams.get("g") || "";
    const isTimer = getSession()?.role === "chronometreur";

    const [info, setInfo] = useState(null);
    const [groupData, setGroupData] = useState(null);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    // Ajout
    const [source, setSource] = useState("swissski");
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Confirmation — le nom de l'entraîneur est mémorisé sur l'appareil.
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [coachName, setCoachName] = useState(() => {
        try {
            return localStorage.getItem("teamcheck-coach") || "";
        } catch {
            return "";
        }
    });
    // Tentative de quitter avec des modifications non confirmées.
    const [leaveBlocked, setLeaveBlocked] = useState(false);

    // Brouillon (07.10.2026) : ajouts / retraits gardés sur cette page et
    // envoyés au serveur UNIQUEMENT à la confirmation. Sans confirmation,
    // rien n'est enregistré : la liste reste dans l'état d'avant.
    const [draftAdds, setDraftAdds] = useState([]); // résultats de recherche
    const [draftRemoves, setDraftRemoves] = useState([]); // clés d'athlètes

    const base = `/api/public/team-check/${encodeURIComponent(eventKey)}`;
    const withT = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}`;

    const call = useCallback(
        async (url, options = {}) => {
            const res = await fetch(withT(url), { ...options, headers: { "Content-Type": "application/json", ...(options.headers || {}) } });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
            return data;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token, eventKey]
    );

    const loadInfo = useCallback(async () => {
        try {
            const d = await call(base);
            setInfo(d);
            // Liste proposée selon le type de course (FIS / Swiss-Ski).
            if (d.sources?.length && !d.sources.includes(source)) setSource(d.sources[0]);
            setError("");
        } catch (err) {
            setError(err.message);
        }
    }, [call, base]);

    const loadGroup = useCallback(async () => {
        if (!group) return setGroupData(null);
        try {
            setGroupData(await call(`${base}/group/${encodeURIComponent(group)}`));
        } catch (err) {
            setError(err.message);
        }
    }, [call, base, group]);

    useEffect(() => {
        loadInfo();
    }, [loadInfo]);
    useEffect(() => {
        loadGroup();
        setResults([]);
        setQuery("");
        setDraftAdds([]);
        setDraftRemoves([]);
    }, [loadGroup]);

    const selectGroup = (g) => {
        if (pending && g !== group) return setLeaveBlocked(true);
        const next = new URLSearchParams(searchParams);
        if (g) next.set("g", g);
        else next.delete("g");
        setSearchParams(next, { replace: true });
        setMessage("");
    };

    const locked = groupData?.status === "confirmed";
    // Modifications en brouillon, pas encore confirmées.
    const pending = draftAdds.length > 0 || draftRemoves.length > 0;

    // Fermer l'onglet / recharger : avertissement du navigateur.
    useEffect(() => {
        if (!pending) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [pending]);

    // Bouton "retour" du navigateur : on reste sur la page et on demande
    // de confirmer.
    useEffect(() => {
        if (!pending) return undefined;
        window.history.pushState({ teamCheckGuard: true }, "");
        const onPopState = () => {
            window.history.pushState({ teamCheckGuard: true }, "");
            setLeaveBlocked(true);
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [pending]);

    const search = async () => {
        if (query.trim().length < 2) return;
        setSearching(true);
        try {
            setResults(await call(`${base}/search?source=${source}&q=${encodeURIComponent(query.trim())}`));
        } catch (err) {
            setError(err.message);
        } finally {
            setSearching(false);
        }
    };

    const add = (r) => {
        const already =
            draftAdds.some((x) => x.source === r.source && String(x.code) === String(r.code)) ||
            (groupData?.athletes || []).some((x) => String(x.fiscode) === String(r.code));
        if (already) return setError(`${r.lastname} ${r.firstname} is already in your list.`);
        setDraftAdds((prev) => [...prev, r]);
        setResults((prev) => prev.filter((x) => x !== r));
        setMessage(`${r.lastname} ${r.firstname} added — not saved until you confirm.`);
    };

    // Retrait (ou annulation du retrait) d'un athlète déjà inscrit.
    const toggleRemove = (a) => setDraftRemoves((prev) => (prev.includes(a.key) ? prev.filter((k) => k !== a.key) : [...prev, a.key]));
    const dropDraftAdd = (r) => setDraftAdds((prev) => prev.filter((x) => x !== r));

    const discardDraft = () => {
        setDraftAdds([]);
        setDraftRemoves([]);
        setLeaveBlocked(false);
        setMessage("Changes discarded — your list is back to its previous state.");
    };

    const confirm = async () => {
        try {
            try {
                localStorage.setItem("teamcheck-coach", coachName.trim());
            } catch {
                /* stockage indisponible : sans importance */
            }
            const d = await call(`${base}/group/${encodeURIComponent(group)}/confirm`, {
                method: "POST",
                body: JSON.stringify({
                    name: coachName,
                    adds: draftAdds.map((r) => ({ source: r.source, code: r.code })),
                    removes: draftRemoves,
                }),
            });
            setDraftAdds([]);
            setDraftRemoves([]);
            setConfirmOpen(false);
            setLeaveBlocked(false);
            setMessage(`Entries confirmed ✓ (${d.added} added, ${d.removed} removed) — thank you!`);
            if (d.warnings?.length) setError(d.warnings.join(" "));
            loadGroup();
            loadInfo();
        } catch (err) {
            setError(err.message);
        }
    };

    // Chronométreur connecté : réouvrir / verrouiller.
    const setLock = async (action) => {
        const res = await fetch(`/api/team-check/${encodeURIComponent(eventKey)}/group/${encodeURIComponent(group)}/${action}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeader() },
        });
        if (!res.ok) return setError(`Error ${res.status}`);
        setMessage(action === "unlock" ? "Group reopened ✓" : "Group locked ✓");
        loadGroup();
        loadInfo();
    };

    const sorted = [...(groupData?.athletes || [])].sort(
        (a, b) => String(a.lastname).localeCompare(String(b.lastname)) || String(a.firstname).localeCompare(String(b.firstname))
    );

    return (
        <Box sx={{ p: 2, maxWidth: 760, mx: "auto" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        Team captains — entry check
                    </Typography>
                    {info && (
                        <Typography variant="body2" color="text.secondary">
                            {info.title}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmojiEventsIcon />}
                    onClick={() =>
                        pending ? setLeaveBlocked(true) : navigate(`/team-check/${encodeURIComponent(eventKey)}/top?t=${encodeURIComponent(token)}`)
                    }
                >
                    Top 15 by points
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ my: 1 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}
            {message && (
                <Alert severity="success" sx={{ my: 1 }} onClose={() => setMessage("")}>
                    {message}
                </Alert>
            )}

            {info && (
                <FormControl fullWidth sx={{ my: 2 }}>
                    <InputLabel>Your {GROUP_LABEL[info.groupField] || "group"}</InputLabel>
                    <Select label={`Your ${GROUP_LABEL[info.groupField] || "group"}`} value={group} onChange={(e) => selectGroup(e.target.value)}>
                        <MenuItem value="">— select —</MenuItem>
                        {info.groups.map((g) => (
                            <MenuItem key={g.key} value={g.key}>
                                {g.key} ({g.count}) {g.status === "confirmed" ? " ✓ confirmed" : ""}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {groupData && (
                <>
                    {pending && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Your changes are <b>not saved yet</b> ({draftAdds.length} to add, {draftRemoves.length} to remove).
                            Press « Confirm my entries » to save them — otherwise they are discarded.
                        </Alert>
                    )}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="h6">
                                {groupData.group} — {sorted.length} athlete(s)
                            </Typography>
                            {locked ? (
                                <Chip icon={<LockIcon />} color="success" label={`Confirmed by ${groupData.confirmedBy || "—"}`} />
                            ) : pending ? (
                                <Chip label="Changes not confirmed" color="error" />
                            ) : (
                                <Chip label="To be confirmed" color="warning" variant="outlined" />
                            )}
                        </Box>

                        {sorted.length === 0 && draftAdds.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No athlete entered in this group.
                            </Typography>
                        )}
                        {sorted.map((a, i) => {
                            const removed = draftRemoves.includes(a.key);
                            return (
                                <Box key={a.key}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, opacity: removed ? 0.6 : 1 }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: "bold", textDecoration: removed ? "line-through" : "none" }}>
                                                {a.lastname} {a.firstname}
                                                {removed && (
                                                    <Chip size="small" color="error" label="to remove" sx={{ ml: 1, textDecoration: "none" }} />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {[a.birthyear, a.gender, a.category, a.club, a.fiscode && `code ${a.fiscode}`, a.points !== "" && a.points != null && `${a.points} pts`]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </Typography>
                                        </Box>
                                        {!locked &&
                                            (removed ? (
                                                <Button size="small" onClick={() => toggleRemove(a)}>
                                                    Undo
                                                </Button>
                                            ) : (
                                                <IconButton color="error" onClick={() => toggleRemove(a)} aria-label="Remove">
                                                    <DeleteIcon />
                                                </IconButton>
                                            ))}
                                    </Box>
                                    {i < sorted.length - 1 && <Divider />}
                                </Box>
                            );
                        })}
                        {draftAdds.map((r) => (
                            <Box key={`${r.source}-${r.code}`} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: "bold" }}>
                                            {r.lastname} {r.firstname}
                                            <Chip size="small" color="success" label="new" sx={{ ml: 1 }} />
                                        </Typography>
                                        <Typography variant="caption" color={r.points == null ? "error" : "text.secondary"}>
                                            {[r.birthyear, r.nation, r.club, `n° ${r.code}`, r.points != null ? `${r.points} pts` : "no points in the list"]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </Typography>
                                    </Box>
                                    <IconButton color="error" onClick={() => dropDraftAdd(r)} aria-label="Remove">
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Paper>

                    {!locked && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                Add an athlete
                            </Typography>
                            {(info?.sources || []).length > 1 ? (
                                <ToggleButtonGroup exclusive size="small" value={source} onChange={(_, v) => v && (setSource(v), setResults([]))} sx={{ mb: 1 }}>
                                    {info.sources.includes("swissski") && <ToggleButton value="swissski">Swiss-Ski list</ToggleButton>}
                                    {info.sources.includes("fis") && <ToggleButton value="fis">FIS list</ToggleButton>}
                                </ToggleButtonGroup>
                            ) : (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    Searching the{" "}
                                    {source === "fis" ? `FIS ${info?.sector === "TM" ? "telemark" : "alpine"}` : "Swiss-Ski"} list
                                    {info?.pointsLabel ? ` (points ${info.pointsLabel[source]})` : ""}
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Name, first name or licence no."
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && search()}
                                />
                                <Button variant="contained" onClick={search} disabled={searching || query.trim().length < 2}>
                                    {searching ? "…" : "Search"}
                                </Button>
                            </Box>
                            {results.map((r) => (
                                <Box key={`${r.source}-${r.code}`} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
                                    <Box>
                                        <Typography>
                                            {r.lastname} {r.firstname}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {[r.birthyear, r.nation, r.club, r.association, `n° ${r.code}`, r.points != null && `${r.points} pts`].filter(Boolean).join(" · ")}
                                        </Typography>
                                    </Box>
                                    <Button size="small" startIcon={<PersonAddIcon />} onClick={() => add(r)}>
                                        Add
                                    </Button>
                                </Box>
                            ))}
                            {query.trim().length >= 2 && !searching && results.length === 0 && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Press Search — if the athlete does not appear, please contact the organiser.
                                </Typography>
                            )}
                        </Paper>
                    )}

                    {!locked ? (
                        <Button fullWidth size="large" variant="contained" color="success" startIcon={<LockIcon />} onClick={() => setConfirmOpen(true)}>
                            Confirm my entries
                        </Button>
                    ) : (
                        <Alert severity="info">
                            Entries confirmed{groupData.confirmedAt ? ` on ${new Date(groupData.confirmedAt).toLocaleString("en-GB")}` : ""}. For any
                            change, please contact the timekeeper.
                        </Alert>
                    )}

                    {isTimer && (
                        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                            {locked ? (
                                <Button variant="outlined" startIcon={<LockOpenIcon />} onClick={() => setLock("unlock")}>
                                    Reopen for changes (timekeeper)
                                </Button>
                            ) : (
                                <Button variant="outlined" startIcon={<LockIcon />} onClick={() => setLock("lock")}>
                                    Lock (timekeeper)
                                </Button>
                            )}
                        </Box>
                    )}
                </>
            )}

            <Dialog open={leaveBlocked} onClose={() => setLeaveBlocked(false)} fullWidth maxWidth="xs">
                <DialogTitle>Unconfirmed changes</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        You changed the entries of <b>{groupData?.group}</b>. Confirm them now, or discard them (the list
                        stays as it was before).
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLeaveBlocked(false)}>Back to my list</Button>
                    <Button color="error" onClick={discardDraft}>
                        Discard changes
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => {
                            setLeaveBlocked(false);
                            setConfirmOpen(true);
                        }}
                    >
                        Confirm now
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Confirm entries</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {groupData?.group}: {sorted.length - draftRemoves.length + draftAdds.length} athlete(s)
                        {pending ? ` (${draftAdds.length} added, ${draftRemoves.length} removed)` : ""}. Once confirmed, the list can no
                        longer be changed (except by the timekeeper).
                    </Typography>
                    <TextField fullWidth autoFocus label="Your name (coach)" value={coachName} onChange={(e) => setCoachName(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={confirm} disabled={!coachName.trim()}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}